import React, { useState } from 'react';
import MouseLightEffect from './MouseLightEffect';
import { useTrack } from '../analytics/TrackingContext';

function PotenciaCarga({ capacidadBateria }) {
  const { trackEvent } = useTrack();
  const [kmDiarios, setKmDiarios] = useState('');
  const [consumo, setConsumo] = useState('');
  const [horasCarga, setHorasCarga] = useState('');
  const [minutosCarga, setMinutosCarga] = useState('');
  const [resultado, setResultado] = useState(null);
  const [animarResultado, setAnimarResultado] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  const calcularPotencia = () => {
    const horas = parseInt(horasCarga) || 0;
    const minutos = parseInt(minutosCarga) || 0;
    const tiempoDecimal = horas + minutos / 60;

    if (!kmDiarios || parseFloat(kmDiarios) <= 0 || !consumo || parseFloat(consumo) <= 0) {
      setErrorMessage('Por favor, introduce todos los datos.');
      setResultado(null);
      trackEvent('calculation_error', {
        screen: 'calculator', section: 'datos_ve', tool: 'potencia_carga',
        properties: { error_code: 'missing_required' },
      });
      return;
    }
    if (tiempoDecimal <= 0) {
      setErrorMessage('El tiempo de carga debe ser mayor que cero.');
      setResultado(null);
      trackEvent('calculation_error', {
        screen: 'calculator', section: 'datos_ve', tool: 'potencia_carga',
        properties: { error_code: 'invalid_range' },
      });
      return;
    }

    const kWhNecesarios = (parseFloat(kmDiarios) * parseFloat(consumo)) / 100;
    const potencia = kWhNecesarios / tiempoDecimal;
    const cap = parseFloat(capacidadBateria);
    const porcentajeBateria = cap > 0 ? ((kWhNecesarios / cap) * 100).toFixed(1) : null;
    setResultado({ potencia: isNaN(potencia) ? 0 : potencia, kWh: kWhNecesarios.toFixed(2), km: kmDiarios, porcentajeBateria, capacidad: cap > 0 ? cap : null });
    setAnimarResultado(true);
    setErrorMessage('');
    trackEvent('calculation_success', {
      screen: 'calculator', section: 'datos_ve', tool: 'potencia_carga',
    });
  };

  return (
    <MouseLightEffect>
    <div className="section-container">

      <div className="tooltip-wrapper" style={{ position: 'absolute', top: '12px', right: '12px' }}>
        <button
          onClick={() => setShowInfo(true)}
          aria-label="Más información"
          style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Kiafontl, Kiafontn, Arial, sans-serif' }}
        >ℹ</button>
        <span className="tooltip-text tooltip-left">Más información</span>
      </div>

      <h2 style={{ textAlign: 'center', width: '100%' }}>¿Cuánta potencia necesito para cargar?</h2>

      {showInfo ? (
        <div className="info-panel">
          <p>Calcula la potencia mínima del cargador que necesitas. Indica los Km que recorres al día, el consumo de tu vehículo y el tiempo del que dispones para cargar. El resultado te dirá cuántos kW debe ofrecer el punto de recarga.</p>
          <div className="button">
            <button className="calc" onClick={() => setShowInfo(false)}>← Volver</button>
          </div>
        </div>
      ) : (
        <>
        <div className="input">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label>Km Recorridos:</label>
              <input
                id="km_diarios_input"
                type="number"
                value={kmDiarios}
                onChange={(e) => setKmDiarios(e.target.value)}
                placeholder="0"
                min={0}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Consumo (kWh/100):</label>
              <input
                id="consumo_input"
                type="number"
                value={consumo}
                onChange={(e) => setConsumo(e.target.value)}
                placeholder="0"
                min={0}
                step="0.1"
              />
            </div>
          </div>
        </div>
        <div className="input">
          <label>Tiempo para cargar:</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              id="horas_potencia_input"
              type="number"
              value={horasCarga}
              onChange={(e) => setHorasCarga(e.target.value)}
              placeholder="0"
              min={0}
              style={{ flex: 1 }}
              aria-label="Horas"
            />
            <span style={{ flexShrink: 0, fontSize: '0.85rem', opacity: 0.7 }}>h</span>
            <input
              id="minutos_potencia_input"
              type="number"
              value={minutosCarga}
              onChange={(e) => setMinutosCarga(e.target.value)}
              placeholder="30"
              min={0}
              max={59}
              style={{ flex: 1 }}
              aria-label="Minutos"
            />
            <span style={{ flexShrink: 0, fontSize: '0.85rem', opacity: 0.7 }}>min</span>
          </div>
        </div>

      <div className="button">
        <button className="calc" onClick={calcularPotencia}>Calcular Potencia</button>
      </div>

      {errorMessage && (
          <div className="error-message" role="alert" aria-live="assertive">
          <p>{errorMessage}</p>
        </div>
      )}

      {resultado !== null && (
          <div className={`res ${animarResultado ? 'resultado-animado' : ''}`} aria-live="polite" aria-atomic="true">
          <p>¿Cuánta potencia necesito para cargar?</p>
          <p><strong>{resultado.potencia.toFixed(2)} kW</strong></p>
          <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>(Para recorrer {resultado.km} Km recorridos necesitas {resultado.kWh} kWh)</p>
          {resultado.porcentajeBateria !== null && (
            <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>(En tu batería de {resultado.capacidad} kWh habrías gastado el {resultado.porcentajeBateria}%)</p>
          )}
          <div className="icon-container">
            <img src={`${process.env.PUBLIC_URL}/images/potencia-carga07.png`} alt="Icono de Potencia" className="potencia-carga" />
          </div>
        </div>
      )}
        </>
      )}
    </div>
    </MouseLightEffect>
  );
}

export default PotenciaCarga;

